import { sortBy } from "lodash";
import { Coupon, CouponType, LineItem, LineItemObject, LineItemType, LineItemVisibility, PaymentMethod } from "types";

export const isVisibleToSeller = (lineItem: LineItem) => {
    return lineItem.visibility === LineItemVisibility.ALL || lineItem.visibility === LineItemVisibility.SELLER;
};

export const isVisibleToTraveler = (lineItem: LineItem) => {
    return lineItem.visibility === LineItemVisibility.ALL || lineItem.visibility === LineItemVisibility.TRAVELER;
};

export const isCancellationProtectionLineItem = (lineItem: Pick<LineItem, "type">) =>
    lineItem.type === LineItemType.CANCELLATION_PROTECTION_FEE;

export const taxFeeTypes = [LineItemType.TAX, LineItemType.FEE];

export const isTaxFee = (lineItem: LineItem) => {
    if ("type" in lineItem && lineItem.type) {
        return taxFeeTypes.includes(lineItem.type);
    }
};

export const getSortedAddonAndDemographicLineItems = (lineItems: LineItem[]) => {
    const sortedDemographicsAndAddon = sortBy(
        lineItems.filter((lineItem) => demographicAddonLineItemTypes.includes(lineItem.type)),
        "sequence",
    );

    const otherLineItems = lineItems.filter((lineItem) => !demographicAddonLineItemTypes.includes(lineItem.type));

    return sortedDemographicsAndAddon.concat(otherLineItems);
};

export const demographicAddonLineItemTypes = [
    LineItemType.DEMOGRAPHIC,
    LineItemType.EQUIPMENT,
    LineItemType.MEMBERSHIP,
    LineItemType.ADDON,
    LineItemType.PICKUP,
    LineItemType.WARE,
];

export const adjustmentLineItemTypes = [LineItemType.ADJUSTMENT, LineItemType.TAXED_ADJUSTMENT];

export const isAdjustmentLineItem = (lineItem: LineItem) => {
    if ("type" in lineItem) {
        return adjustmentLineItemTypes.includes(lineItem.type);
    }
};

export const isVisibleLineItem = (lineItem: { visibility: LineItemVisibility }) => {
    const { visibility } = lineItem;
    if (visibility === LineItemVisibility.NONE) {
        return false;
    }

    return true;
};

export const isStoreCreditCoupon = (coupon?: Partial<Coupon>) => coupon?.source === "store_credit";
export const isGiftCoupon = (coupon?: Partial<Coupon>) => coupon?.source === "gift";
export const isPaymentCoupon = (coupon?: Partial<Coupon>) => isStoreCreditCoupon(coupon) || isGiftCoupon(coupon);
export const isVoucherCoupon = (coupon?: Partial<Coupon>) => coupon?.type === CouponType.VOUCHER;

export const isLineItemWithNoPrice = (lineItem: LineItem): boolean => {
    const noPriceLineItemTypes: LineItemType[] = [LineItemType.DEMOGRAPHIC, LineItemType.WARE, LineItemType.EQUIPMENT];
    return noPriceLineItemTypes.includes(lineItem.type) && !lineItem.price && !!lineItem.quantity;
};

export const getLineItemsByObject = (lineItems: LineItem[], object: LineItemObject): LineItem[] => {
    return lineItems.filter((lineItem) => lineItem.object === object);
};

export const getMaskedLineItemName = (lineItem: LineItem) => {
    if (
        lineItem.type === LineItemType.COUPON_DISCOUNT &&
        !isPaymentCoupon(lineItem?.coupon) &&
        !isVoucherCoupon(lineItem?.coupon)
    ) {
        return "Coupon";
    }

    if (lineItem.type === LineItemType.AFFILIATE_DISCOUNT) {
        return "Partner Discount";
    }

    if (lineItem.type === LineItemType.AFFILIATE_DEPOSIT) {
        return "Partner Deposit";
    }

    if (lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.GIFT) {
        return "Gift";
    }

    if (lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.STORE_CREDIT) {
        return "Store Credit";
    }

    if (lineItem.refundToStoreCredit) {
        return "Store Credit";
    }

    return lineItem.name;
};
